import { useState, useEffect, MouseEvent, ChangeEvent } from 'react'
// @ts-ignore
import { createPortal } from 'react-dom'
import { useTipCreator } from 'src/hooks/useTipCreator'
import styled from 'styled-components'

interface TipButtonProps {
  creatorId: string
  creatorUsername?: string
  defaultAmount?: number // Amount in dollars (e.g., 5 = $5.00)
  allowCustomAmount?: boolean
  buttonText?: string
  className?: string
  autoOpen?: boolean
  context?: 'live'
}

export default function TipButton({
  creatorId,
  creatorUsername,
  defaultAmount = 5,
  allowCustomAmount = true,
  buttonText = 'Tip Creator',
  className,
  autoOpen = false,
  context
}: TipButtonProps) {
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    if (autoOpen) setShowModal(true)
  }, [autoOpen])
  const [customAmount, setCustomAmount] = useState(defaultAmount.toString())
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const { tipCreator } = useTipCreator()

  const suggestedAmounts = [5, 10, 25, 50, 100, 200]

  const handleTip = (amountInDollars: number) => {
    const amountInCents = Math.round(amountInDollars * 100)
    tipCreator({
      creatorId,
      creatorUsername,
      amount: amountInCents,
      context
    })
    setShowModal(false)
  }

  const handleCustomTip = () => {
    const amount = parseFloat(customAmount)
    if (isNaN(amount) || amount < 5) {
      alert('Please enter a valid amount (minimum $5)')
      return
    }
    if (amount > 100000) {
      alert('Maximum tip amount is $100,000')
      return
    }
    handleTip(amount)
  }

  return (
    <>
      <Button onClick={() => setShowModal(true)} className={className}>
        {buttonText}
      </Button>

      {showModal && createPortal(
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Tip {creatorUsername || 'Creator'}</h2>
              <CloseButton onClick={() => setShowModal(false)}>&times;</CloseButton>
            </ModalHeader>

            <ModalBody>
              <p className="text-gray-600 mb-4">
                Support this creator with a tip!
              </p>

              <AmountGrid>
                {suggestedAmounts.map((amount) => (
                  <AmountButton
                    key={amount}
                    selected={selectedAmount === amount}
                    onClick={() => {
                      setSelectedAmount(amount)
                      handleTip(amount)
                    }}
                  >
                    ${amount}
                  </AmountButton>
                ))}
              </AmountGrid>

              {allowCustomAmount && (
                <>
                  <Divider>or</Divider>
                  <CustomAmountSection>
                    <label htmlFor="custom-amount" className="block text-sm font-medium mb-2">
                      Custom Amount
                    </label>
                    <CustomAmountInput>
                      <span className="dollar-sign">$</span>
                      <input
                        id="custom-amount"
                        type="number"
                        min="5"
                        max="100000"
                        step="0.01"
                        value={customAmount}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setCustomAmount(e.target.value)}
                        placeholder="0.00"
                      />
                    </CustomAmountInput>
                    <CustomTipButton onClick={handleCustomTip}>
                      Send Custom Tip
                    </CustomTipButton>
                  </CustomAmountSection>
                </>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>,
        document.body
      )}
    </>
  )
}

const Button = styled.button`
  background: #000;
  color: #fff;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: background 0.2s;

  &:hover {
    background: #333;
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;

  h2 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: #9ca3af;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;

  &:hover {
    color: #000;
  }
`

const ModalBody = styled.div`
  padding: 20px;
`

const AmountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
`

const AmountButton = styled.button<{ selected: boolean }>`
  background: ${(props) => (props.selected ? '#000' : '#f3f4f6')};
  color: ${(props) => (props.selected ? '#fff' : '#000')};
  border: 2px solid ${(props) => (props.selected ? '#000' : 'transparent')};
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.selected ? '#000' : '#e5e7eb')};
  }
`

const Divider = styled.div`
  text-align: center;
  margin: 24px 0;
  color: #9ca3af;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 40%;
    height: 1px;
    background: #e5e7eb;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
`

const CustomAmountSection = styled.div`
  margin-top: 20px;
`

const CustomAmountInput = styled.div`
  position: relative;
  margin-bottom: 16px;

  .dollar-sign {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 18px;
    font-weight: 600;
    color: #6b7280;
  }

  input {
    width: 100%;
    padding: 12px 12px 12px 36px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 18px;
    font-weight: 600;

    &:focus {
      outline: none;
      border-color: #000;
    }
  }
`

const CustomTipButton = styled.button`
  width: 100%;
  background: #000;
  color: #fff;
  padding: 14px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: background 0.2s;

  &:hover {
    background: #333;
  }
`

